import { useState } from "react";
import axios from "axios";
import { Loader2, ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal that asks for the subscriber's email, then opens the Stripe billing portal.
 * Trigger with a controlled `open` prop; parent sets `onOpenChange(false)` to close.
 */
export default function ManageSubscriptionModal({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/billing-portal`, {
        email,
        return_url: window.location.origin + "/pricing",
      });
      window.location.href = data.portal_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn’t open the billing portal. Please try again.");
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Manage subscription"
      data-testid="manage-subscription-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-3xl p-6 md:p-8 nb-shadow-lg relative"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          data-testid="manage-subscription-close-btn"
          className="absolute top-4 right-4 h-9 w-9 rounded-xl border-2 border-[#1A1A1A] bg-white nb-shadow-sm flex items-center justify-center"
        >
          <X size={18} strokeWidth={2.5} />
        </button>
        <p className="font-mono text-[11px] uppercase tracking-widest text-[#FF007F] font-bold">
          Manage · Stripe billing portal
        </p>
        <h2 className="mt-2 font-display font-black text-3xl leading-tight tracking-tighter">
          Update your subscription.
        </h2>
        <p className="mt-3 text-sm text-[#4A4A4A]">
          Enter the email you used at checkout. We’ll open your Stripe billing
          portal — where you can update payment methods, download invoices,
          switch plans or cancel.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@school.edu"
            data-testid="manage-subscription-email-input"
            className="w-full border-2 border-[#1A1A1A] rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700]"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="manage-subscription-submit-btn"
            className="inline-flex items-center justify-center gap-2 w-full bg-[#0055FF] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press disabled:opacity-70"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ArrowUpRight size={16} strokeWidth={2.5} />
            )}
            {loading ? "Opening portal…" : "Open portal"}
          </button>
        </form>
        <p className="mt-4 text-[11px] font-mono uppercase tracking-widest text-[#4A4A4A]">
          Powered by Stripe · secure customer portal
        </p>
      </div>
    </div>
  );
}
